import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';

import { useExternalInputCommandListKeyboard } from '@/common/hooks/useExternalInputCommandListKeyboard';
import { ctw } from '@/common/utils/ctw';
import { Command, CommandEmpty } from '../Command';
import { CommandGroup, CommandItem, CommandList } from '../Command/Command';
import { Popover, PopoverContent } from '../Popover';
import { getDisplayText } from './helpers';

export type ISearchableDropdownOption = {
  value: string;
  label: string;
  displayLabel?: ReactNode;
  searchKeywords?: string[];
};

type ISearchableDropdownProps = {
  options: ISearchableDropdownOption[];
  value: string | undefined;
  disabled?: boolean;
  placeholder?: string;
  optionNotFoundText?: string;
  testId?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  disablePortal?: boolean;
  allowCustomValue?: boolean;
  showAllOptions?: boolean;
};

const getOptionScore = (option: ISearchableDropdownOption, search: string): number => {
  if (!search) return 1;

  const searchLower = search.toLowerCase();
  const labelLower = option.label.toLowerCase();

  if (option.searchKeywords?.some(kw => kw.toLowerCase() === searchLower)) {
    return 100;
  }

  if (labelLower.startsWith(searchLower)) {
    return 75;
  }

  if (labelLower.includes(searchLower)) {
    return 50;
  }

  if (option.searchKeywords?.some(kw => kw.toLowerCase().includes(searchLower))) {
    return 25;
  }

  return 0;
};

export const SearchableDropdown = ({
  options,
  value,
  placeholder = 'Select...',
  optionNotFoundText = 'No options found',
  testId,
  disabled = false,
  onChange,
  onBlur,
  onFocus,
  disablePortal = false,
  allowCustomValue = false,
  showAllOptions = false,
}: ISearchableDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const hasFocusFiredRef = useRef(false);
  const isClosingRef = useRef(false);
  const pendingSelectRef = useRef(false);

  const displayText = useMemo(() => getDisplayText(options, value), [options, value]);

  const filteredAndSortedOptions = useMemo(() => {
    if (!search) return options;

    const scored = options.map(option => ({ option, score: getOptionScore(option, search) }));

    if (showAllOptions) {
      return scored.sort((a, b) => b.score - a.score).map(({ option }) => option);
    }

    return scored
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ option }) => option);
  }, [options, search, showAllOptions]);

  const showAddCustom = allowCustomValue && search.trim().length > 0;

  const customRowValue = showAddCustom ? `__custom__${search}` : null;

  const selectableItemValues = useMemo(() => {
    const keys = filteredAndSortedOptions.map(option => option.label);

    if (customRowValue) {
      keys.push(customRowValue);
    }

    return keys;
  }, [filteredAndSortedOptions, customRowValue]);

  const closeDropdown = useCallback(() => {
    isClosingRef.current = true;
    setOpen(false);
    setSearch('');

    // Defer blur to next frame so DOM settles (popover removal, focus changes).
    // handleInputBlur checks isClosingRef to avoid double-firing onBlur.
    requestAnimationFrame(() => {
      isClosingRef.current = false;

      // Only fire onBlur if focus has actually left the input (i.e. user finished
      // interacting). If the input still has focus (e.g. Escape pressed), defer
      // onBlur until focus truly leaves via handleInputBlur.
      if (document.activeElement !== inputRef.current) {
        hasFocusFiredRef.current = false;
        onBlur?.();
      }
    });
  }, [onBlur]);

  const handleSelect = useCallback(
    (currentValue: string) => {
      const selectedOption = options.find(option => option.label === currentValue);

      if (selectedOption) {
        onChange(selectedOption.value);
      }

      closeDropdown();
    },
    [onChange, options, closeDropdown],
  );

  const handleCustomValueAdd = useCallback(() => {
    const trimmedSearch = search.trim();

    if (trimmedSearch) {
      onChange(trimmedSearch);
    }

    closeDropdown();
  }, [onChange, search, closeDropdown]);

  const handleCommitFromKeyboard = useCallback(
    (current: string) => {
      if (customRowValue !== null && current === customRowValue) {
        handleCustomValueAdd();
      } else {
        handleSelect(current);
      }
    },
    [customRowValue, handleCustomValueAdd, handleSelect],
  );

  const { highlightedValue, setHighlightedValue, handleListKeyDown } =
    useExternalInputCommandListKeyboard({
      open,
      selectableValues: selectableItemValues,
      onCommitValue: handleCommitFromKeyboard,
      onOpenRequest: () => {
        setSearch(displayText);
        setOpen(true);
      },
    });

  const handleInputFocus = useCallback(() => {
    if (isClosingRef.current) return;

    if (!hasFocusFiredRef.current) {
      hasFocusFiredRef.current = true;
      onFocus?.();
    }

    // Prepare search text but don't open — wait for user interaction (typing or chevron click).
    setSearch(displayText);

    // Select all text on focus for easy replacement.
    pendingSelectRef.current = true;

    requestAnimationFrame(() => {
      if (pendingSelectRef.current) {
        inputRef.current?.select();
      }

      pendingSelectRef.current = false;
    });
  }, [onFocus, displayText]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      pendingSelectRef.current = false;
      setSearch(e.target.value);

      if (!open) {
        setOpen(true);
      }
    },
    [open],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        closeDropdown();

        return;
      }

      handleListKeyDown(e);
    },
    [handleListKeyDown, closeDropdown],
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        closeDropdown();
      }
    },
    [closeDropdown],
  );

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // closeDropdown already schedules onBlur via rAF — don't double-fire.
      if (isClosingRef.current) return;

      // Check if focus is moving to something inside the popover content
      const relatedTarget = e.relatedTarget as HTMLElement | null;

      if (relatedTarget) {
        // If the related target is inside our container or inside popover content, ignore
        if (containerRef.current?.contains(relatedTarget)) {
          return;
        }

        // Check if the related target is inside a popover content (may be portaled)
        if (relatedTarget.closest('[data-radix-popper-content-wrapper]')) {
          return;
        }
      }

      // Focus is leaving the component entirely — close dropdown and fire onBlur.
      if (open) {
        closeDropdown();

        return;
      }

      hasFocusFiredRef.current = false;
      onBlur?.();
    },
    [open, onBlur, closeDropdown],
  );

  const handleInputClick = useCallback(() => {
    if (!open && !isClosingRef.current) {
      setSearch(displayText);
      setOpen(true);
    }
  }, [open, displayText]);

  const handleChevronMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      if (open) {
        closeDropdown();
      } else {
        setSearch(displayText);
        setOpen(true);
        inputRef.current?.focus();
      }
    },
    [open, closeDropdown, displayText],
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverPrimitive.Anchor asChild>
        <div
          ref={containerRef}
          role="combobox"
          aria-expanded={open}
          data-testid={testId ? `${testId}-trigger` : undefined}
          className={ctw(
            'border-input bg-background focus-within:ring-ring flex h-9 w-full items-center rounded-md border px-3 shadow-sm transition-colors focus-within:ring-1',
            {
              'cursor-not-allowed opacity-50': disabled,
            },
          )}
        >
          <input
            ref={inputRef}
            type="text"
            className={ctw(
              'placeholder:text-muted-foreground flex-1 truncate bg-transparent text-sm outline-none',
              {
                'cursor-not-allowed': disabled,
              },
            )}
            placeholder={placeholder}
            value={open ? search : displayText}
            disabled={disabled}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onClick={handleInputClick}
            onKeyDown={handleInputKeyDown}
            data-testid={testId ? `${testId}-search-input` : undefined}
          />
          <ChevronsUpDown
            className="ml-2 size-4 shrink-0 cursor-pointer opacity-50"
            onMouseDown={handleChevronMouseDown}
          />
        </div>
      </PopoverPrimitive.Anchor>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        side="bottom"
        sideOffset={4}
        avoidCollisions={true}
        disablePortal={disablePortal}
        collisionBoundary={typeof window !== 'undefined' ? document.body : undefined}
        onOpenAutoFocus={e => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
        onCloseAutoFocus={e => {
          e.preventDefault();
        }}
        onInteractOutside={e => {
          if (containerRef.current?.contains(e.target as Node)) {
            e.preventDefault();
          }
        }}
      >
        <Command
          shouldFilter={false}
          value={highlightedValue}
          onValueChange={setHighlightedValue}
          loop
        >
          <CommandList ref={listRef} style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredAndSortedOptions.length === 0 && !showAddCustom && (
              <CommandEmpty>{optionNotFoundText}</CommandEmpty>
            )}
            <CommandGroup>
              {filteredAndSortedOptions.map(option => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                  onSelect={() => handleSelect(option.label)}
                >
                  {option.displayLabel ?? option.label}
                  <Check
                    className={ctw(
                      'ml-auto size-4',
                      value === option.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </CommandItem>
              ))}
              {showAddCustom && customRowValue && (
                <CommandItem
                  value={customRowValue}
                  data-testid={testId ? `${testId}-add-custom` : undefined}
                  onSelect={handleCustomValueAdd}
                >
                  <Plus className="mr-2 size-4" />
                  {`Add "${search.trim()}"`}
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
